<template>
  <div class="modal fade" tabindex="-1" id="BillToReceiveModal"
       data-bs-keyboard="false" data-bs-backdrop="static">
    <div class="modal-dialog modal-xl">
      <div class="modal-content">
        <form @submit.prevent="submitForm">
          <div class="modal-header">
            <h5 class="modal-title">
              {{ isSettlementOnly
                ? 'Baixar título (lote TISS)'
                : (bill?.id ? (bill.status === 'negotiated' ? 'Conta Negociada' : 'Editar Conta a Receber') : 'Nova Conta a Receber') }}
            </h5>
            <button type="button" class="btn-close" @click="closeModal()"></button>
          </div>

          <div class="modal-body">
            <div v-if="isSettlementOnly" class="alert alert-info mb-4">
              Conta gerada por lote de guias: é permitido baixar o título e alterar a categoria.
            </div>
            <div class="row">
              <div class="col-md-12">
                <div class="mb-3">
                  <label class="form-label required">Descrição</label>
                  <input v-model="form.description" type="text" class="form-control" :required="!isSettlementOnly" :disabled="isSettlementOnly">
                </div>
              </div>

              <div class="col-md-12">
                <div class="mb-3">
                  <label class="form-label required">Tipo de Recebimento</label>
                  <select v-model="form.payment_type" class="form-select" :required="!isSettlementOnly" @change="onPaymentTypeChange" :disabled="isSettlementOnly">
                    <option value="single">À Vista</option>
                    <option value="installment">Parcelado</option>
                  </select>
                </div>
              </div>
              <template v-if="form.payment_type === 'installment'">
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label required">Quantidade de Parcelas</label>
                    <input v-model.number="form.total_installments" type="number" min="2" class="form-control" required :disabled="isSettlementOnly">
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Intervalo entre Parcelas (meses)</label>
                    <input v-model.number="form.installment_interval" type="number" min="1" class="form-control" value="1" :disabled="isSettlementOnly">
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Valor Total</label>
                    <input v-model.number="form.amount" type="number" min="0" step="0.01" class="form-control" 
                           :disabled="isSettlementOnly || form.installment_value > 0">
                    <small class="text-muted">Deixe em branco se informar valor da parcela</small>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Valor por Parcela</label>
                    <input v-model.number="form.installment_value" type="number" min="0" step="0.01" class="form-control"
                           @input="calculateTotalFromInstallment" :disabled="isSettlementOnly">
                    <small class="text-muted">Deixe em branco se informar valor total</small>
                  </div>
                </div>
                <div class="col-md-12" v-if="form.total_installments && (form.amount || form.installment_value)">
                  <div class="alert alert-info">
                    <strong>Resumo:</strong> 
                    {{ form.total_installments }}x de 
                    R$ {{ formatCurrency(calculateInstallmentValue()) }} = 
                    R$ {{ formatCurrency(calculateTotalValue()) }}
                  </div>
                </div>
              </template>

              <template v-if="form.payment_type === 'single'">
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label required">Valor</label>
                    <input v-model.number="form.amount" type="number" min="0" step="0.01" class="form-control" :required="!isSettlementOnly" :disabled="isSettlementOnly">
                  </div>
                </div>
              </template>

              <div class="col-md-6" v-if="form.payment_type !== 'installment'">
                <div class="mb-3">
                  <label class="form-label">Desconto</label>
                  <input v-model.number="form.discount" type="number" min="0" step="0.01" class="form-control" value="0" :disabled="isSettlementOnly">
                </div>
              </div>

              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label required">Data de Vencimento</label>
                  <input
                    v-if="isSettlementOnly"
                    type="text"
                    class="form-control"
                    :value="formatDateDisplay(form.due_date)"
                    disabled
                  >
                  <p-calendar
                    v-else
                    class="fw-bold form-control-sm form-control" v-model="form.due_date"
                    placeholder="DD/MM/YYYY"
                    dateFormat="dd/mm/yy"
                    showIcon
                    iconDisplay="input"
                    required
                  />
                </div>
              </div>

              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label">Data de Lançamento</label>
                  <input
                    v-if="isSettlementOnly"
                    type="text"
                    class="form-control"
                    :value="formatDateDisplay(form.issue_date)"
                    disabled
                  >
                  <p-calendar
                    v-else
                    class="fw-bold form-control-sm form-control" v-model="form.issue_date"
                    placeholder="DD/MM/YYYY"
                    dateFormat="dd/mm/yy"
                    showIcon
                    iconDisplay="input"
                  />
                </div>
              </div>

              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label required">Categoria</label>
                  <div class="input-group">
                    <select v-model="form.transaction_category_id" class="form-select" required>
                      <option value="">Selecione uma categoria</option>
                      <option v-for="category in categories" :key="category.id" :value="category.id">
                        {{ category.name }}
                      </option>
                    </select>
                    <button type="button" class="btn btn-outline-primary" 
                            @click="openCategoryPage" title="Cadastrar categoria">
                      <i class="bi bi-plus-lg"></i>
                    </button>
                  </div>
                </div>
              </div>

              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label">Cliente</label>
                  <div class="input-group">
                    <select v-model="form.customer_id" class="form-select" :disabled="isSettlementOnly">
                      <option value="">Selecione um cliente</option>
                      <option v-for="customer in customers" :key="customer.id" :value="customer.id">
                        {{ customer.name }}
                      </option>
                    </select>
                    <button type="button" class="btn btn-outline-primary" 
                            @click="openCustomerPage" title="Cadastrar cliente" :disabled="isSettlementOnly">
                      <i class="bi bi-plus-lg"></i>
                    </button>
                  </div>
                </div>
              </div>

              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label">Conta Bancária</label>
                  <div class="input-group">
                    <select v-model="form.bank_account_id" class="form-select">
                      <option value="">Selecione uma conta</option>
                      <option v-for="account in bankAccounts" :key="account.id" :value="account.id">
                        {{ account.name }}
                      </option>
                    </select>
                    <button type="button" class="btn btn-outline-primary" 
                            @click="openBankAccountPage" title="Cadastrar conta bancária">
                      <i class="bi bi-plus-lg"></i>
                    </button>
                  </div>
                </div>
              </div>

              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label required">Status</label>
                  <select v-model="form.status" class="form-select" required :disabled="form.status === 'negotiated'">
                    <option value="pending">Pendente</option>
                    <option value="received">Recebido</option>
                    <option v-if="!isSettlementOnly" value="negotiated">Negociado</option>
                  </select>
                </div>
              </div>

              <div class="col-md-6" v-if="form.status === 'received'">
                <div class="mb-3">
                  <label class="form-label">Data de Recebimento</label>
                  <p-calendar
                    class="fw-bold form-control-sm form-control" 
                    v-model="form.received_at"
                    placeholder="DD/MM/YYYY"
                    dateFormat="dd/mm/yy"
                    showIcon
                    iconDisplay="input"
                  />
                </div>
              </div>

              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label">Forma de Recebimento</label>
                  <select v-model="form.payment_method_id" class="form-select">
                    <option value="">Selecione uma forma de recebimento</option>
                    <option v-for="paymentMethod in paymentMethods" :key="paymentMethod.id_paymeth" :value="paymentMethod.id_paymeth">
                      {{ paymentMethod.paymeth_name }}
                    </option>
                  </select>
                </div>
              </div>

              <div class="col-md-12">
                <div class="mb-3">
                  <label class="form-label">Observações</label>
                  <textarea v-model="form.notes" class="form-control" rows="3" placeholder="Observações adicionais..."></textarea>
                </div>
              </div>
            </div>
          </div>

          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeModal()">Cancelar</button>
            <button type="submit" class="btn btn-primary" :disabled="form.status === 'negotiated'">
              {{ isSettlementOnly ? 'Confirmar baixa' : 'Salvar' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import http from "@/helpers/http";
import Modal from "@/helpers/modal";
import Calendar from "primevue/calendar";
import Alert from "@/helpers/alert";

export default {
  components: {
    'p-calendar': Calendar
  },
  props: {
    show: Boolean,
    bill: Object,
    settlementOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      form: {
        description: "",
        amount: 0,
        discount: 0,
        due_date: null,
        issue_date: null,
        transaction_category_id: "",
        customer_id: "",
        bank_account_id: "",
        status: "pending",
        payment_method: "",
        payment_method_id: "",
        payment_type: "single",
        total_installments: null,
        installment_value: null,
        installment_interval: 1,
        notes: "",
        received_at: null
      },
      categories: [],
      customers: [],
      bankAccounts: [],
      paymentMethods: []
    };
  },
  computed: {
    isSettlementOnly() {
      return this.settlementOnly || Boolean(this.bill?.from_tiss_batch || this.bill?.id_tiss_batch);
    },
  },
  watch: {
    bill: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.form = {
            description: newVal.description || "",
            amount: newVal.amount || 0,
            discount: newVal.discount || 0,
            due_date: newVal.due_date ? new Date(newVal.due_date) : null,
            issue_date: newVal.issue_date ? new Date(newVal.issue_date) : null,
            transaction_category_id: newVal.transaction_category_id || "",
            customer_id: newVal.customer_id || "",
            bank_account_id: newVal.bank_account_id || "",
            status: newVal.status || "pending",
            payment_method: newVal.payment_method || "",
            payment_method_id: newVal.payment_method_id || "",
            payment_type: newVal.payment_type || "single",
            total_installments: newVal.total_installments || null,
            installment_value: null,
            installment_interval: 1,
            notes: newVal.notes || "",
            received_at: newVal.received_at_date ? new Date(newVal.received_at_date) : null
          };
        } else {
          this.resetForm();
        }
      }
    },
    show(newValue) {
      if (newValue) {
        this.loadData();
        Modal.toggle('#BillToReceiveModal')
      }
    }
  },
  methods: {
    resetForm() {
      this.form = {
        description: "",
        amount: 0,
        discount: 0,
        due_date: null,
        issue_date: null,
        transaction_category_id: "",
        customer_id: "",
        bank_account_id: "",
        status: "pending",
        payment_method: "",
        payment_method_id: "",
        payment_type: "single",
        total_installments: null,
        installment_value: null,
        installment_interval: 1,
        notes: "",
        received_at: null
      };
    },
    onPaymentTypeChange() {
      if (this.form.payment_type === 'single') {
        this.form.total_installments = null;
        this.form.installment_value = null;
      }
    },
    calculateInstallmentValue() {
      if (this.form.installment_value) {
        return this.form.installment_value;
      }
      if (this.form.amount && this.form.total_installments) {
        return (this.form.amount - (this.form.discount || 0)) / this.form.total_installments;
      }
      return 0;
    },
    calculateTotalValue() {
      if (this.form.installment_value && this.form.total_installments) {
        return this.form.installment_value * this.form.total_installments;
      }
      return this.form.amount || 0;
    },
    calculateTotalFromInstallment() {
      if (this.form.installment_value && this.form.total_installments) {
        this.form.amount = this.form.installment_value * this.form.total_installments;
      }
    },
    formatCurrency(value) {
      return parseFloat(value || 0).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    loadData() {
      const placeId = this.$store.state.auth.place.id_place;

      http.get(`/places/${placeId}/finances/transaction-categories`, { params: { type: "income" } })
        .then(response => {
          this.categories = response.data.filter(cat => cat.active !== false);
        })
        .catch(() => {
          this.categories = [];
        });

      http.get(`/places/${placeId}/payment-methods`)
        .then(response => {
          this.paymentMethods = response.data || [];
        })
        .catch(() => {
          this.paymentMethods = [];
        });

      http.get(`/places/${placeId}/finances/customers`)
        .then(response => {
          this.customers = response.data.filter(c => c.active !== false);
        })
        .catch(() => {
          this.customers = [];
        });

      http.get(`/places/${placeId}/finances/bank-accounts`)
        .then(response => {
          this.bankAccounts = response.data.filter(acc => acc.active !== false);
        })
        .catch(() => {
          this.bankAccounts = [];
        });
    },
    openCategoryPage() {
      this.closeModal();
      this.$router.push('/finance/transaction-categories');
    },
    openCustomerPage() {
      this.closeModal();
      this.$router.push('/finance/customers');
    },
    openBankAccountPage() {
      this.closeModal();
      this.$router.push('/finance/bank-accounts');
    },
    submitForm() {
      if (this.form.status === 'negotiated') {
        Alert.toastError("Esta conta está negociada e não pode ser editada.");
        return;
      }

      const placeId = this.$store.state.auth.place.id_place;
      const method = this.bill?.id ? "put" : "post";
      const url = this.bill?.id 
        ? `/places/${placeId}/finances/bills-to-receive/${this.bill.id}` 
        : `/places/${placeId}/finances/bills-to-receive`;

      const data = {
        ...this.form,
        due_date: this.form.due_date ? this.formatDateForAPI(this.form.due_date) : null,
        issue_date: this.form.issue_date ? this.formatDateForAPI(this.form.issue_date) : null,
        received_at: this.form.status === 'received' && this.form.received_at 
          ? this.formatDateForAPI(this.form.received_at) 
          : null
      };

      http[method](url, data).then(() => {
        Alert.toastSuccess(
          this.isSettlementOnly
            ? "Baixa registrada com sucesso!"
            : (this.bill?.id ? "Conta atualizada com sucesso!" : "Conta cadastrada com sucesso!")
        );
        this.$emit("saved");
        this.closeModal();
      }).catch(e => {
        Alert.toastError(e.response?.data?.message || e.response?.data?.error || "Erro ao salvar conta.");
      });
    },
    formatDateForAPI(date) {
      if (!date) return null;
      const d = new Date(date);
      return d.toISOString().split('T')[0];
    },
    formatDateDisplay(date) {
      if (!date) return '-';
      const d = date instanceof Date ? date : new Date(date);
      if (Number.isNaN(d.getTime())) return '-';
      return d.toLocaleDateString('pt-BR');
    },
    closeModal() {
      Modal.toggle('#BillToReceiveModal')
      this.$emit("close");
    }
  }
};
</script>

<style scoped>
.required::after {
  content: " *";
  color: red;
}
.input-group .btn {
  border-left: 0;
}
</style>
